import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { showNotification, showError } from '../utils/notificationHandler';

export default function SetPassword() {
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [setupComplete, setSetupComplete] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: []
  });
  const captchaRef = useRef(null);
  const history = useHistory();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Check if user is authenticated
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        showNotification('Please log in first', 'error');
        history.push('/login');
        return;
      }

      // Check if password is already set
      const { data: account, error } = await supabase
        .from('accounts')
        .select('password_set')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error checking password status:', error);
        return;
      }

      if (account?.password_set) {
        // Password already set, redirect to dashboard
        history.push('/user/dashboard');
      }
    };

    checkAuth();
  }, [history]);

  useEffect(() => {
    // Calculate password strength
    const calculatePasswordStrength = (password) => {
      const feedback = [];
      let score = 0;

      if (password.length >= 8) {
        score += 1;
      } else {
        feedback.push('Password must be at least 8 characters long');
      }

      if (/[a-z]/.test(password)) {
        score += 1;
      } else {
        feedback.push('Password must contain lowercase letters');
      }

      if (/[A-Z]/.test(password)) {
        score += 1;
      } else {
        feedback.push('Password must contain uppercase letters');
      }

      if (/[0-9]/.test(password)) {
        score += 1;
      } else {
        feedback.push('Password must contain numbers');
      }

      if (/[^A-Za-z0-9]/.test(password)) {
        score += 1;
      } else {
        feedback.push('Password must contain special characters');
      }

      setPasswordStrength({ score, feedback });
    };

    if (password) {
      calculatePasswordStrength(password);
    } else {
      setPasswordStrength({ score: 0, feedback: [] });
    }
  }, [password]);

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validation
      if (!password || !confirmPassword) {
        showNotification('Please fill in all fields', 'error');
        return;
      }

      if (password !== confirmPassword) {
        showNotification('Passwords do not match', 'error');
        return;
      }

      if (passwordStrength.score < 3) {
        showNotification('Password is too weak. Please choose a stronger password.', 'error');
        return;
      }

      if (!captchaToken) {
        showNotification('Please complete the captcha', 'error');
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Update the user's password
      const { error: passwordError } = await supabase.auth.updateUser({
        password: password
      });

      if (passwordError) {
        console.error('Password update error:', passwordError);
        throw passwordError;
      }

      console.log('Password updated successfully');
      
      // Update the password_set flag in the accounts table
      const { error: accountError } = await supabase
        .from('accounts')
        .update({ password_set: true })
        .eq('id', user.id);

      if (accountError) {
        console.error('Error updating password_set flag:', accountError);
        throw accountError;
      }

      console.log('Password setup complete');
      
      setSetupComplete(true);
      showNotification('Password set successfully! Redirecting to dashboard...', 'success');

      // Auto-redirect to dashboard after 2 seconds
      setTimeout(() => {
        history.push('/user/dashboard');
      }, 2000);

    } catch (error) {
      console.error('Password setup error:', error);
      
      // Check if the error is due to weak password
      if (error.name === 'AuthWeakPasswordError' || 
          error.message?.includes('weak') || 
          error.message?.includes('easy to guess')) {
        showNotification(
          'This password is too common or has appeared in data breaches. Please choose a more unique password.',
          'error',
          {
            autoClose: 8000,
          }
        );
        
        // Show additional guidance
        setTimeout(() => {
          showNotification(
            'Try using a random combination of words, numbers and symbols that you haven\'t used before.',
            'info',
            {
              autoClose: 8000,
            }
          );
        }, 500);
      } else {
        // Generic error for other types of failures
        showError(error, 'Failed to set password. Please try again.');
      }
      
      // Reset captcha on error
      if (captchaRef.current) {
        captchaRef.current.resetCaptcha();
      }
      setCaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength.score <= 2) return 'bg-red-500';
    if (passwordStrength.score <= 3) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength.score <= 2) return 'Weak';
    if (passwordStrength.score <= 3) return 'Good';
    return 'Strong';
  };

  if (setupComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-dark py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
            <h2 className="mt-6 text-center text-2xl md:text-3xl font-extrabold text-brand">
              Password Setup Complete!
            </h2>
            <p className="mt-2 text-center text-sm text-gray-300">
              Your password has been successfully set. You can now access the partner portal.
            </p>
            <p className="mt-2 text-center text-xs text-gray-400">
              Redirecting to dashboard...
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-dark py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <img
            src="/Logo2.png"
            alt="Company Logo"
            className="mx-auto h-24 md:h-36 w-auto"
          />
          <h2 className="mt-6 text-center text-2xl md:text-3xl font-extrabold text-brand">
            Welcome to Partner Portal
          </h2>
          <p className="mt-2 text-center text-sm text-gray-300">
            Please set a password for your account
          </p>
          <p className="mt-1 text-center text-xs text-gray-400">
            You'll use this password to log in to the portal
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-[#242222] rounded-lg shadow-2xl border border-brand/20 p-6 sm:p-8"
        >
          <form 
            className="space-y-6" 
            onSubmit={handleSetPassword}
          >
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="password" className="sr-only">New Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-brand-dark" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    className="appearance-none rounded-none relative block w-full px-3 py-2 pl-10 pr-10 border border-brand placeholder-gray-500 text-brand-dark rounded-t-md focus:outline-none focus:ring-brand focus:border-brand focus:z-10 sm:text-sm bg-white"
                    placeholder="New Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-brand-dark" />
                    ) : (
                      <Eye className="h-5 w-5 text-brand-dark" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirm-password" className="sr-only">Confirm Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-brand-dark" />
                  </div>
                  <input
                    id="confirm-password"
                    name="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    className="appearance-none rounded-none relative block w-full px-3 py-2 pl-10 pr-10 border border-brand placeholder-gray-500 text-brand-dark rounded-b-md focus:outline-none focus:ring-brand focus:border-brand focus:z-10 sm:text-sm bg-white"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-brand-dark" />
                    ) : (
                      <Eye className="h-5 w-5 text-brand-dark" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Password Strength Indicator */}
            {password && (
              <div className="mt-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-300">Password Strength:</span>
                  <span className={`text-sm font-medium ${
                    passwordStrength.score <= 2 ? 'text-red-500' :
                    passwordStrength.score <= 3 ? 'text-yellow-500' : 'text-green-500'
                  }`}>
                    {getPasswordStrengthText()}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${getPasswordStrengthColor()}`}
                    style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                  ></div>
                </div>
                {passwordStrength.feedback.length > 0 && (
                  <div className="mt-2">
                    <ul className="text-xs text-gray-400 space-y-1">
                      {passwordStrength.feedback.map((feedback, index) => (
                        <li key={index}>• {feedback}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Password Match Indicator */}
            {confirmPassword && (
              <div className="flex items-center">
                {password === confirmPassword ? (
                  <div className="flex items-center text-green-500">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    <span className="text-sm">Passwords match</span>
                  </div>
                ) : (
                  <div className="flex items-center text-red-500">
                    <XCircle className="h-4 w-4 mr-2" />
                    <span className="text-sm">Passwords do not match</span>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-center">
              <HCaptcha
                sitekey={window.REACT_APP_HCAPTCHA_SITE_KEY || process.env.REACT_APP_HCAPTCHA_SITE_KEY}
                onVerify={(token) => setCaptchaToken(token)}
                ref={captchaRef}
                size={isMobile ? 'compact' : 'normal'}
              />
            </div>

            <div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-brand-dark bg-brand hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand transition-colors duration-200"
                disabled={loading || !captchaToken || password !== confirmPassword || passwordStrength.score < 3}
              >
                {loading ? 'Setting Password...' : 'Set Password'}
              </motion.button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
